import {InputAdornment, TextField} from '@mui/material';
import Box from '@mui/material/Box';
// eslint-disable-next-line wandb/no-deprecated-imports
import {MOON_500} from '@wandb/weave/common/css/color.styles';
import {fuzzyMatchHighlight} from '@wandb/weave/common/util/fuzzyMatch';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {AutoComplete} from '@wandb/weave/components/Form/AutoComplete';
import {
  IconClose,
  IconRecentClock,
  IconSearch,
} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useEffect, useRef, useState} from 'react';

import {useColorMode} from '../../../util/useColorMode';
import {trackSearchHistory} from '../../PanelBank/search/searchHistoryEvents';
import type {
  BaseSearchHistoryItem,
  OptionTypeBase,
  RunSearchHistoryItem,
} from '../../PanelBank/types';

const TEXTAREA_MIN_ROWS_HEIGHT = 32;

interface SearchHistoryProps<
  BaseSearchHistoryItem,
  TOption extends OptionTypeBase,
> {
  searchHistory: BaseSearchHistoryItem[];
  onOptionChange: (value: TOption) => void;
  onFocus?: () => void;
  onBlur?: (inputValue: string) => void;
  setInputValue:
    | React.Dispatch<React.SetStateAction<string>>
    | ((newSearchQuery: string) => void);
  inputValue: string;
  customActionButton?: React.JSX.Element;
  placeholderText: string;
  onClear?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  maxWidth?: number;
  selectedOption: TOption | null;
  setSelectedOption?: React.Dispatch<React.SetStateAction<TOption | null>>;
  minWidth?: number;
  searchQuery?: string;
  isCollapsible?: boolean;
  maxItems: number;
  focusOnPageLoad?: boolean;
  dataTest?: string;
}

export const SearchHistory = <
  T extends BaseSearchHistoryItem,
  TOption extends OptionTypeBase,
>({
  searchHistory,
  onOptionChange,
  onFocus,
  onBlur,
  inputValue,
  setInputValue,
  customActionButton,
  placeholderText,
  onClear,
  maxWidth,
  minWidth = 100,
  selectedOption,
  setSelectedOption,
  searchQuery,
  isCollapsible,
  maxItems,
  focusOnPageLoad,
  dataTest,
}: SearchHistoryProps<T, TOption>) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [autocompleteWidth, setAutocompleteWidth] = useState<number>(0);

  const hasLoaded = useRef(false);
  const isInitialAutoFocus = useRef(focusOnPageLoad);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const {isDarkMode} = useColorMode();
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [scrollHeight, setScrollHeight] = useState(0);
  const autocompleteRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Reset isInitialAutoFocus on page load to prevent it opening after switching tabs
    isInitialAutoFocus.current = focusOnPageLoad;
  }, [focusOnPageLoad]);

  const updateAutocompleteWidth = () => {
    if (autocompleteRef.current) {
      setAutocompleteWidth(autocompleteRef.current.offsetWidth);
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      setScrollHeight(inputRef.current.scrollHeight);
    }
  }, [isExpanded, inputValue]);

  useEffect(() => {
    if (inputRef.current) {
      setIsOverflowing(scrollHeight > TEXTAREA_MIN_ROWS_HEIGHT);
    }
  }, [scrollHeight, inputValue]);

  useEffect(() => {
    const handleResize = () => {
      updateAutocompleteWidth();
    };
    updateAutocompleteWidth();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [inputValue]);

  // Sync inputValue with searchQuery only on initial load
  useEffect(() => {
    if (!hasLoaded.current && searchQuery) {
      setInputValue(searchQuery);
      hasLoaded.current = true;
    }
  }, [searchQuery, setInputValue]);

  const getLabel = (dropdownValue: string) => {
    const match = fuzzyMatchHighlight(
      dropdownValue,
      inputValue,
      undefined,
      true
    );
    return (
      <Tailwind>
        <div className="flex items-start">
          <div className="moon-800 mr-8 mt-3 flex-shrink-0">
            <IconRecentClock width={18} height={18} />
          </div>
          <div className="line-clamp-5 break-all">
            {match == null || selectedOption !== null ? dropdownValue : match}
          </div>
        </div>
      </Tailwind>
    );
  };

  const searchHistoryItems: TOption[] = searchHistory
    .filter(s => s.query !== inputValue)
    .slice(0, maxItems)
    .map(s => ({
      value: s.query,
      label: s.query,
      ...(isRunSearchHistoryItem(s) ? {isRegex: s.isRegex} : {}),
    })) as TOption[];

  const handleInputChange = (newInputValue: string, reason: string) => {
    if (reason === 'reset') {
      return;
    }
    setInputValue(newInputValue);
    setSelectedOption?.(null);
    setIsOpen(true);
  };

  const handleOptionChange = (newValue: TOption | null) => {
    if (!newValue) {
      return;
    }

    onOptionChange(newValue);
    setInputValue(newValue.value);

    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' || event.key === 'Enter') {
      setIsOpen(false);
    }
    if (event.key === 'Enter') {
      // Blur the input to remove focus
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLDivElement, Element>) => {
    setIsExpanded(true);
    // Prevent the menu from opening on the first focus (autofocus)
    if (isInitialAutoFocus.current) {
      isInitialAutoFocus.current = false;
    } else {
      setIsOpen(true);
    }

    requestAnimationFrame(() => {
      if (inputRef.current && isCollapsible) {
        const position = inputValue.length;
        inputRef.current.setSelectionRange(position, position);
      }
      inputRef.current?.focus();
      onFocus?.();
    });
  };

  const onClearHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsOpen(false);
    onClear?.(event);
    setSelectedOption?.(null);
    setInputValue('');
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  return (
    <AutoComplete<TOption>
      autocompleteRef={autocompleteRef}
      freeSolo
      openOnFocus={false}
      open={isOpen}
      isDarkMode={isDarkMode}
      maxWidth={maxWidth}
      minWidth={minWidth}
      value={selectedOption ?? ''}
      inputValue={inputValue}
      onInputChange={(_, newValue, reason) => {
        handleInputChange(newValue, reason);
      }}
      onChange={(_, newValue) => {
        // MUI Autocomplete's typing is wrong https://github.com/mui/material-ui/issues/33093
        // it won't ever be a string when freeSolo is true.
        handleOptionChange(newValue as TOption | null);
      }}
      options={searchHistoryItems}
      onKeyDown={handleKeyDown}
      hasInputValue={inputValue !== ''}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          {getLabel(option.value)}
        </Box>
      )}
      noOptionsText={null}
      renderInput={params => {
        return (
          <TextField
            autoFocus={focusOnPageLoad}
            data-test={dataTest}
            multiline={isCollapsible ? isExpanded : false}
            minRows={1}
            maxRows={6}
            inputRef={inputRef}
            {...params}
            variant="outlined"
            placeholder={placeholderText}
            sx={{
              '.MuiInputBase-root': {
                alignItems: 'flex-start',
              },
              '.MuiInputAdornment-root': {
                alignItems: 'flex-start',
                marginTop: '4px',
              },
              '.MuiAutocomplete-endAdornment': {
                top: '0%',
                transform: 'translate(0, 0%) !important',
                alignItems: 'flex-start !important',
                marginTop: '4px',
              },
              textarea: {
                resize: 'none',
                lineHeight: isExpanded && !isOverflowing ? '32px' : '160%', // when input changes to multiline, the text is offcenter
                marginY: isExpanded && isOverflowing ? '4px' : '0px',
              },
            }}
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <IconSearch
                    color={MOON_500}
                    style={{
                      flexShrink: 0,
                      marginTop: '6px',
                      marginRight: '4px',
                    }}
                  />
                ),
                endAdornment: autocompleteWidth >= minWidth && (
                  <>
                    <InputAdornment position="end">
                      {customActionButton}
                    </InputAdornment>
                    <div className="flex flex-col">
                      <InputAdornment position="end">
                        {inputValue ? params.InputProps.endAdornment : null}
                      </InputAdornment>
                      {isOverflowing && isExpanded && inputValue !== '' && (
                        <Button
                          size="small"
                          variant="ghost"
                          icon="expand-right"
                          className="absolute bottom-6 right-7 -rotate-90"
                          onClick={(
                            event: React.MouseEvent<HTMLButtonElement>
                          ) => {
                            event.stopPropagation();
                            setIsExpanded(false);
                            inputRef?.current?.blur();
                            trackSearchHistory({
                              action: 'Collapsed input',
                              location: 'workspace run selector',
                              inputLength: inputValue.length,
                              inputHeight: inputRef.current?.offsetHeight,
                            });
                          }}
                        />
                      )}
                    </div>
                  </>
                ),
              },
            }}
          />
        );
      }}
      slotProps={{
        clearIndicator: {
          onClick: onClearHandler,
          component: IconClose,
          style: {
            visibility: inputValue ? 'visible' : 'hidden',
          },
        },
      }}
      fullWidth
      onFocus={event => {
        handleFocus(event);
      }}
      onBlur={() => {
        setIsOpen(false);
        if (inputValue) {
          onBlur?.(inputValue);
        }
      }}
    />
  );
};

export function isRunSearchHistoryItem(
  item: BaseSearchHistoryItem
): item is RunSearchHistoryItem {
  return (item as RunSearchHistoryItem).isRegex !== undefined;
}
